import {
  anniversariesFromBirthdays,
  dateFromKey,
  dateKeyOf,
  exceptionDatesByEventId,
  expandEventOccurrences,
  isMilestoneAnniversary,
  sortAnniversariesByCountdown,
  type AnniversaryIcon,
  type EventOccurrence,
  type ScheduleEvent,
} from "@couples/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Heart, Plus, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import AnniversaryCountdown from "../components/AnniversaryCountdown";
import AnniversaryModal from "../components/AnniversaryModal";
import Confetti from "../components/Confetti";
import DaySheet from "../components/DaySheet";
import EventSheet from "../components/EventSheet";
import MonthCalendarGrid from "../components/MonthCalendarGrid";
import RecurrenceScopeDialog, { type RecurrenceScope } from "../components/RecurrenceScopeDialog";
import ScreenHeader from "../components/ScreenHeader";
import UpcomingEventsList from "../components/UpcomingEventsList";
import { errorMessageOf, useAuth } from "../lib/auth";
import { fetchBirthdays } from "../lib/birthdays";
import { DEFAULT_PARTNER_COLOR, DEFAULT_SELF_COLOR, mixHexColors } from "../lib/color";
import { useConfirm } from "../lib/confirm";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { fetchAnniversaries } from "../lib/schedule/anniversaries";
import { fetchEventCategories } from "../lib/schedule/categories";
import {
  createEventOccurrenceOverride,
  fetchEventExceptions,
  skipEventOccurrence,
} from "../lib/schedule/eventExceptions";
import {
  createEvent,
  deleteEvent,
  fetchEvents,
  updateEvent,
  type EventInput,
} from "../lib/schedule/events";
import { anniversarySuffix } from "../lib/schedule/format";
import { downloadScheduleIcs } from "../lib/schedule/ics";
import { useAnniversaryCelebration } from "../lib/schedule/useAnniversaryCelebration";
import { useToast } from "../lib/toast";
import AnniversaryManager from "./AnniversaryManager";

/** カレンダー日付セルのバッジは横に並べられる数が限られるため、同じ日に3件以上あっても先頭2件だけ出す */
const ANNIV_BADGES_MAX = 2;

function addDays(dateKey: string, days: number): string {
  const date = dateFromKey(dateKey);
  return dateKeyOf(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days));
}

interface EventSheetState {
  editing?: ScheduleEvent;
  initialDate: string;
  /** 繰り返し予定のうち、この回だけを編集・差し替える(→ openEditOccurrence) */
  occurrenceOnly?: boolean;
}

interface ScopePromptState {
  event: ScheduleEvent;
  occurrenceStartDate: string;
}

interface Props {
  /** 通知から飛んできたときの対象の予定。無ければ null */
  targetEventId?: string | null;
  /** targetEventId を消化し終えたら呼ぶ(→ Shell) */
  onConsumeTarget?: () => void;
}

export default function Schedule({ targetEventId = null, onConsumeTarget }: Props) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const today = useMemo(() => new Date(), []);
  const todayKey = dateKeyOf(today);

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [eventSheet, setEventSheet] = useState<EventSheetState | null>(null);
  const [scopePrompt, setScopePrompt] = useState<ScopePromptState | null>(null);
  // scopePrompt (state) は表示用。resolve は re-render を待たずに参照できるよう ref で持つ
  // (→ lib/confirm.tsx の decide.current と同じ理由)。
  const scopeDecide = useRef<((scope: RecurrenceScope | null) => void) | null>(null);
  const [showAnniversaries, setShowAnniversaries] = useState(false);
  const [eventBusy, setEventBusy] = useState(false);
  const [upcomingCategoryId, setUpcomingCategoryId] = useState<string | null>(null);

  const selfId = session?.user.id;
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const events = useQuery({ queryKey: queryKeys.events, queryFn: fetchEvents });
  const eventExceptions = useQuery({
    queryKey: queryKeys.eventExceptions,
    queryFn: fetchEventExceptions,
  });
  const categories = useQuery({
    queryKey: queryKeys.eventCategories,
    queryFn: fetchEventCategories,
  });
  const anniversaries = useQuery({
    queryKey: queryKeys.anniversaries,
    queryFn: fetchAnniversaries,
  });
  const birthdays = useQuery({ queryKey: queryKeys.birthdays, queryFn: fetchBirthdays });

  // 誕生日は Settings で入力したプロフィールの値から毎回作る。
  // anniversaries テーブルには書き込まない(→ packages/shared/src/schedule.ts)
  const allAnniversaries = useMemo(
    () => [...(anniversaries.data ?? []), ...anniversariesFromBirthdays(birthdays.data ?? [])],
    [anniversaries.data, birthdays.data],
  );

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of categories.data ?? []) map.set(category.id, category.name);
    return map;
  }, [categories.data]);

  const selfProfile = profiles.data?.find((profile) => profile.id === selfId);
  const partnerProfile = profiles.data?.find((profile) => profile.id !== selfId);
  const partnerId = partnerProfile?.id ?? null;
  const selfName = selfProfile?.displayName ?? "あなた";
  const partnerName = partnerProfile?.displayName ?? "パートナー";
  const selfColor = selfProfile?.favoriteColor ?? DEFAULT_SELF_COLOR;
  const partnerColor = partnerProfile?.favoriteColor ?? DEFAULT_PARTNER_COLOR;
  const bothColor = mixHexColors(selfColor, partnerColor);

  const monthStartKey = dateKeyOf(new Date(year, month, 1));
  const monthEndKey = dateKeyOf(new Date(year, month + 1, 0));

  // 繰り返し予定の「この回だけ」の例外。展開時にこの回をスキップするために使う
  // (差し替え先は独立した単発の予定として events に含まれているので、それはそのまま展開される)
  const exceptionDates = useMemo(
    () => exceptionDatesByEventId(eventExceptions.data ?? []),
    [eventExceptions.data],
  );

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, EventOccurrence[]>();
    for (const occurrence of expandEventOccurrences(
      events.data ?? [],
      monthStartKey,
      monthEndKey,
      undefined,
      exceptionDates,
    )) {
      const bucket = map.get(occurrence.date);
      if (bucket === undefined) map.set(occurrence.date, [occurrence]);
      else bucket.push(occurrence);
    }
    return map;
  }, [events.data, monthStartKey, monthEndKey, exceptionDates]);

  const anniversaryIconsInMonth = useMemo(() => {
    const map = new Map<string, AnniversaryIcon[]>();
    for (const anniversary of allAnniversaries) {
      const [, annivMonth, annivDay] = anniversary.baseDate.split("-");
      const key = `${year}-${annivMonth}-${annivDay}`;
      const icons = map.get(key);
      if (icons === undefined) map.set(key, [anniversary.icon]);
      else if (icons.length < ANNIV_BADGES_MAX) icons.push(anniversary.icon);
    }
    return map;
  }, [allAnniversaries, year]);

  const upcoming = useMemo(() => {
    const windowEnd = addDays(todayKey, 90);
    const occurrences = expandEventOccurrences(
      events.data ?? [],
      todayKey,
      windowEnd,
      undefined,
      exceptionDates,
    );
    // 日を跨ぐ予定は日ごとに複数件展開されるが、一覧では1件にまとめる
    // (でないと同じ予定が何日ぶんも並んで残りの予定を押し出してしまう)。
    const seenEventIds = new Set<string>();
    const deduped: EventOccurrence[] = [];
    for (const occurrence of occurrences) {
      if (upcomingCategoryId !== null && occurrence.event.categoryId !== upcomingCategoryId)
        continue;
      if (seenEventIds.has(occurrence.event.id)) continue;
      seenEventIds.add(occurrence.event.id);
      deduped.push(occurrence);
    }
    return deduped.slice(0, 4);
  }, [events.data, todayKey, upcomingCategoryId, exceptionDates]);

  // 「近日の予定」を日付ごとの見出しでまとめる。upcoming は日付順なので隣接する同日をまとめるだけでよい。
  const upcomingGroups = useMemo(() => {
    const groups: { date: string; items: EventOccurrence[] }[] = [];
    for (const occurrence of upcoming) {
      const last = groups.at(-1);
      if (last !== undefined && last.date === occurrence.date) {
        last.items.push(occurrence);
      } else {
        groups.push({ date: occurrence.date, items: [occurrence] });
      }
    }
    return groups;
  }, [upcoming]);

  const countdown = sortAnniversariesByCountdown(allAnniversaries, today);
  const nearestToday = countdown[0]?.days === 0 ? countdown[0] : undefined;
  const isAnniversaryToday = nearestToday !== undefined;
  const isMilestoneToday =
    nearestToday !== undefined &&
    isMilestoneAnniversary(nearestToday.anniversary.kind, nearestToday.occurrence.count);

  const { showConfetti, showCelebration, dismissCelebration, replayCelebration } =
    useAnniversaryCelebration(isAnniversaryToday, isMilestoneToday, selfId, todayKey);

  /** カレンダー(予定+記念日+誕生日)を ICS ファイルとして書き出す(→ ScreenHeader のメニュー) */
  function handleExportIcs() {
    if (events.data === undefined || eventExceptions.data === undefined) return;
    try {
      const count = downloadScheduleIcs(
        events.data,
        eventExceptions.data,
        allAnniversaries,
        categoryNameById,
      );
      toast(`${String(count)}件を書き出しました。`);
    } catch (error) {
      toast(errorMessageOf(error), "error");
    }
  }

  /**
   * 日別シートを開く。「近日の予定」は表示中の月の外を指すことがあるため、
   * 開く前に表示月をその日付に合わせる
   * (合わせないと、その月ぶんしか展開していない occurrencesByDate に無く空に見える)。
   */
  function openDate(dateKey: string) {
    const target = dateFromKey(dateKey);
    setYear(target.getFullYear());
    setMonth(target.getMonth());
    setSelectedDate(dateKey);
  }

  // 予定追加の通知を押したときの遷移先。events の読み込みを待ってから
  // 該当の予定を探し、その開始日を開く(events は全件取得なのでここで引ける)
  useEffect(() => {
    if (targetEventId === null || events.data === undefined) return;
    const target = events.data.find((event) => event.id === targetEventId);
    if (target !== undefined) openDate(dateKeyOf(new Date(target.startsAt)));
    onConsumeTarget?.();
  }, [targetEventId, events.data, onConsumeTarget]);

  function shiftMonth(delta: number) {
    let m = month + delta;
    let y = year;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setMonth(m);
    setYear(y);
  }

  /**
   * 繰り返し予定の回を編集で開くとき、「この回だけ」か「すべての回」かを最初に聞く
   * (→ RecurrenceScopeDialog)。ここで決めた scope を保存・削除の両方に使い回すため、
   * 予定シートを開いた時点で `occurrenceOnly` として固定する。
   */
  function askRecurrenceScope(
    event: ScheduleEvent,
    occurrenceStartDate: string,
  ): Promise<RecurrenceScope | null> {
    return new Promise((resolve) => {
      // 前の問いが残っていたら、答えないまま捨てられるので取り消し扱いにする
      scopeDecide.current?.(null);
      scopeDecide.current = resolve;
      setScopePrompt({ event, occurrenceStartDate });
    });
  }

  function answerRecurrenceScope(scope: RecurrenceScope | null) {
    scopeDecide.current?.(scope);
    scopeDecide.current = null;
    setScopePrompt(null);
  }

  async function openEditOccurrence(occurrence: EventOccurrence) {
    const { event, startDate } = occurrence;
    if (event.recurrenceFreq === null) {
      setEventSheet({ editing: event, initialDate: startDate });
      return;
    }
    const scope = await askRecurrenceScope(event, startDate);
    if (scope === null) return;
    setEventSheet({
      editing: event,
      initialDate: startDate,
      occurrenceOnly: scope === "occurrence",
    });
  }

  async function handleSaveEvent(input: EventInput) {
    setEventBusy(true);
    try {
      if (eventSheet?.occurrenceOnly === true && eventSheet.editing !== undefined) {
        await createEventOccurrenceOverride(eventSheet.editing.id, eventSheet.initialDate, input);
        await queryClient.invalidateQueries({ queryKey: queryKeys.events });
        await queryClient.invalidateQueries({ queryKey: queryKeys.eventExceptions });
        toast("この回だけ変更しました。");
      } else if (eventSheet?.editing !== undefined) {
        await updateEvent(eventSheet.editing.id, input);
        await queryClient.invalidateQueries({ queryKey: queryKeys.events });
        toast("予定を変更しました。");
      } else {
        await createEvent(input);
        await queryClient.invalidateQueries({ queryKey: queryKeys.events });
        toast("予定を追加しました。");
      }
      setEventSheet(null);
    } catch (error) {
      toast(errorMessageOf(error), "error");
    } finally {
      setEventBusy(false);
    }
  }

  async function handleDeleteEvent() {
    const editing = eventSheet?.editing;
    if (editing === undefined) return;

    if (eventSheet?.occurrenceOnly === true) {
      const ok = await confirm({
        message: `「${editing.title}」のこの回だけ消します。`,
        confirmLabel: "この回を消す",
        tone: "danger",
      });
      if (!ok) return;
      setEventBusy(true);
      try {
        await skipEventOccurrence(editing.id, eventSheet.initialDate);
        await queryClient.invalidateQueries({ queryKey: queryKeys.eventExceptions });
        toast("この回を消しました。");
        setEventSheet(null);
      } catch (error) {
        toast(errorMessageOf(error), "error");
      } finally {
        setEventBusy(false);
      }
      return;
    }

    const message =
      editing.recurrenceFreq !== null
        ? `「${editing.title}」を消します。\n繰り返しの予定は全ての回が消えます。`
        : `「${editing.title}」を消します。`;
    const ok = await confirm({ message, confirmLabel: "予定を消す", tone: "danger" });
    if (!ok) return;

    setEventBusy(true);
    try {
      await deleteEvent(editing.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.events });
      await queryClient.invalidateQueries({ queryKey: queryKeys.eventExceptions });
      toast("予定を消しました。");
      setEventSheet(null);
    } catch (error) {
      toast(errorMessageOf(error), "error");
    } finally {
      setEventBusy(false);
    }
  }

  return (
    <>
      {showConfetti && <Confetti dense={isMilestoneToday} />}

      {showCelebration && nearestToday !== undefined && (
        <AnniversaryModal
          title={nearestToday.anniversary.title}
          suffix={anniversarySuffix(nearestToday.anniversary.kind, nearestToday.occurrence.count)}
          icon={nearestToday.anniversary.icon}
          onClose={dismissCelebration}
        />
      )}

      {scopePrompt !== null && (
        <RecurrenceScopeDialog
          title={scopePrompt.event.title}
          onChoose={answerRecurrenceScope}
          onCancel={() => {
            answerRecurrenceScope(null);
          }}
        />
      )}

      <ScreenHeader
        navKey="schedule"
        actions={[
          {
            label: "予定を追加",
            icon: Plus,
            onSelect: () => {
              setEventSheet({ initialDate: todayKey });
            },
            primary: true,
          },
        ]}
        menu={[
          {
            label: "記念日を管理",
            icon: Heart,
            onSelect: () => {
              setShowAnniversaries(true);
            },
          },
          {
            label: "カレンダーを書き出し(.ics)",
            icon: Download,
            onSelect: handleExportIcs,
          },
          // 動作確認用。記念日当日だけ出す(→ lib/schedule/celebration.ts)
          ...(isAnniversaryToday
            ? [
                {
                  label: "記念日演出をやり直す",
                  icon: RefreshCw,
                  onSelect: replayCelebration,
                },
              ]
            : []),
        ]}
      />

      <div className="relative flex-1">
        <div className="flex flex-col gap-4 px-4 pt-3.5 pb-24">
          {(events.isError || eventExceptions.isError) && (
            <p role="alert" className="text-sm text-[var(--color-plum)]">
              予定を取得できませんでした。
            </p>
          )}
          {(anniversaries.isError || birthdays.isError) && (
            <p role="alert" className="text-sm text-[var(--color-plum)]">
              記念日を取得できませんでした。
            </p>
          )}

          <AnniversaryCountdown
            countdown={countdown}
            showConfetti={showConfetti}
            onOpenAll={() => {
              setShowAnniversaries(true);
            }}
          />

          <MonthCalendarGrid
            year={year}
            month={month}
            todayKey={todayKey}
            selectedDate={selectedDate}
            occurrencesByDate={occurrencesByDate}
            anniversaryIconsInMonth={anniversaryIconsInMonth}
            categories={categories.data}
            selfId={selfId}
            selfColor={selfColor}
            partnerColor={partnerColor}
            bothColor={bothColor}
            onShiftMonth={shiftMonth}
            onOpenDate={openDate}
          />

          <UpcomingEventsList
            categories={categories.data}
            upcomingCategoryId={upcomingCategoryId}
            onCategoryChange={setUpcomingCategoryId}
            groups={upcomingGroups}
            todayKey={todayKey}
            selfId={selfId}
            selfName={selfName}
            partnerName={partnerName}
            selfColor={selfColor}
            partnerColor={partnerColor}
            bothColor={bothColor}
            onOpenOccurrence={(occurrence) => {
              void openEditOccurrence(occurrence);
            }}
          />
        </div>

        {/* 一覧が伸びるほど下に沈む absolute ではなく、ビューポート基準の fixed で
            常にスクロール位置に関わらず親指の届く位置に留める。PC はヘッダーの主操作ボタンが受け持つ */}
        <div className="pointer-events-none fixed inset-x-0 z-40 mx-auto flex max-w-[var(--content-w)] justify-end px-4 bottom-[calc(4rem+env(safe-area-inset-bottom))] xl:hidden">
          <button
            type="button"
            onClick={() => {
              setEventSheet({ initialDate: todayKey });
            }}
            aria-label="予定を追加"
            className="pointer-events-auto flex size-[46px] items-center justify-center rounded-full bg-[var(--color-teal)] shadow-lg"
          >
            <Plus className="size-5 text-white" aria-hidden />
          </button>
        </div>
      </div>

      {showAnniversaries && (
        <AnniversaryManager
          onClose={() => {
            setShowAnniversaries(false);
          }}
        />
      )}

      {selectedDate !== null && selfId !== undefined && (
        <DaySheet
          date={selectedDate}
          occurrences={occurrencesByDate.get(selectedDate) ?? []}
          selfId={selfId}
          selfName={selfName}
          partnerName={partnerName}
          selfColor={selfColor}
          partnerColor={partnerColor}
          categoryNameById={categoryNameById}
          onClose={() => {
            setSelectedDate(null);
          }}
          onAdd={() => {
            setEventSheet({ initialDate: selectedDate });
          }}
          onEditEvent={(occurrence) => {
            void openEditOccurrence(occurrence);
          }}
          onNavigate={(direction) => {
            openDate(addDays(selectedDate, direction === "next" ? 1 : -1));
          }}
        />
      )}

      {eventSheet !== null && selfId !== undefined && (
        <EventSheet
          editing={eventSheet.editing}
          initialDate={eventSheet.initialDate}
          occurrenceOnly={eventSheet.occurrenceOnly === true}
          exceptions={eventExceptions.data ?? []}
          selfId={selfId}
          selfName={selfName}
          partnerId={partnerId}
          partnerName={partnerName}
          selfColor={selfColor}
          partnerColor={partnerColor}
          busy={eventBusy}
          onSave={(input) => void handleSaveEvent(input)}
          onDelete={eventSheet.editing !== undefined ? () => void handleDeleteEvent() : undefined}
          onClose={() => {
            setEventSheet(null);
          }}
        />
      )}
    </>
  );
}
